/**
 * Pastel Color Palette Configuration for Dashboard Components
 * Based on API Security Dashboard design patterns
 */

// Primary Severity Palette - Lighter versions of New Severity Icons
export const SEVERITY_COLORS = {
  critical: '#DC6B8A', // Much deeper pink-red
  high: '#ED7760', // Much deeper coral-orange
  medium: '#FF9550', // Much deeper orange
  low: '#FFC444', // Much deeper yellow
  other: '#E582AB', // Much deeper rose pink
  info: '#5AB3FF', // Much deeper blue
  warning: '#FF7D52', // Much deeper orange
  success: '#72B87D', // Much deeper green
};

// Secondary Categorical Palette - Used for distinct categories in charts
export const CATEGORICAL_COLORS = {
  purple: '#8469C5', // Much deeper lavender purple
  lightBlue: '#5AA4E0', // Much deeper sky blue
  lightGreen: '#72B87D', // Much deeper mint green
  yellow: '#FFD020', // Much deeper lemon yellow
  orange: '#FFA540', // Much deeper peach orange
  mutedBlue: '#6F7EC2', // Much deeper periwinkle blue
  pink: '#E582AB', // Much deeper rose pink
  teal: '#4AADA5', // Much deeper teal
  indigo: '#6F7EC2', // Much deeper indigo
  amber: '#FFB840', // Much deeper amber
  gray: '#909090',
  brown: '#96766D',
  redPink: '#BC5F80',
};

// Chart-specific color configurations
export const CHART_COLORS = {
  // Bar chart colors
  bar: {
    critical: SEVERITY_COLORS.critical,
    high: SEVERITY_COLORS.high,
    medium: SEVERITY_COLORS.medium,
    low: SEVERITY_COLORS.low,
    other: SEVERITY_COLORS.other,
  },

  // Line chart colors
  line: {
    critical: SEVERITY_COLORS.critical,
    high: SEVERITY_COLORS.high,
    medium: SEVERITY_COLORS.medium,
    low: SEVERITY_COLORS.low,
    other: SEVERITY_COLORS.other,
  },

  // Pie/Donut chart colors
  pie: {
    critical: SEVERITY_COLORS.critical,
    high: SEVERITY_COLORS.high,
    medium: SEVERITY_COLORS.medium,
    low: SEVERITY_COLORS.low,
    other: SEVERITY_COLORS.other,
  },

  // Area chart colors
  area: {
    critical: SEVERITY_COLORS.critical,
    high: SEVERITY_COLORS.high,
    medium: SEVERITY_COLORS.medium,
    low: SEVERITY_COLORS.low,
    other: SEVERITY_COLORS.other,
  },

  // Funnel chart colors
  funnel: {
    total: '#FFEBEE', // Very light pinkish-orange
    filtered: '#FFAB91', // Mid-range orange
    final: '#FF7043', // Deeper orange-red
  },
};

// Resource type colors for specific categorizations
export const RESOURCE_COLORS = {
  kubernetes: CATEGORICAL_COLORS.purple,
  scalingGroups: CATEGORICAL_COLORS.lightBlue,
  hosts: CATEGORICAL_COLORS.lightGreen,
  containers: CATEGORICAL_COLORS.yellow,
  services: CATEGORICAL_COLORS.orange,
  networks: CATEGORICAL_COLORS.mutedBlue,
};

// Compliance score colors
export const COMPLIANCE_SCORE_COLORS = {
  excellent: '#FFC444', // Much deeper yellow for 90%+
  good: '#FF9550', // Much deeper orange for 50-89%
  poor: '#ED7760', // Much deeper coral for 31-49%
  critical: '#DC6B8A', // Much deeper pink-red
  others: '#8A9CB0', // Much deeper slate blue for <30%
};

// Status colors
export const STATUS_COLORS = {
  active: '#72B87D', // Much deeper green
  inactive: '#909090', // Much deeper gray
  pending: '#FFA540', // Much deeper orange
  error: '#FF6A7C', // Much deeper pink-red
  success: '#72B87D', // Much deeper green
  warning: '#FF7D52', // Much deeper orange
  info: '#5AB3FF', // Much deeper blue
};

// Background and UI colors
export const UI_COLORS = {
  background: {
    primary: '#FFFFFF', // White
    secondary: '#F5F5F5', // Light gray
    tertiary: '#EEEEEE', // Very light gray
  },
  text: {
    primary: '#333333', // Dark gray/black
    secondary: '#666666', // Medium gray
    tertiary: '#AAAAAA', // Light gray
  },
  border: {
    primary: '#EEEEEE', // Very light gray
    secondary: '#E0E0E0', // Light gray
    focus: '#9362d9', // Blue
  },
};

// Gradient configurations
export const GRADIENTS = {
  severity: {
    critical: 'linear-gradient(135deg, #F4A3B8 0%, #F8A896 100%)',
    high: 'linear-gradient(135deg, #F8A896 0%, #FFC085 100%)',
    medium: 'linear-gradient(135deg, #FFC085 0%, #FFE99F 100%)',
    low: 'linear-gradient(135deg, #FFE99F 0%, #A5D6A7 100%)',
  },
  funnel: 'linear-gradient(90deg, #FFEBEE 0%, #FFAB91 50%, #FF7043 100%)',
};

// Severity background colors for components
export const SEVERITY_BACKGROUND_COLORS = {
  CRITICAL: {
    background: '#F9CCD9',
    border: '#DC6B8A',
    text: SEVERITY_COLORS.critical,
    chip: SEVERITY_COLORS.critical,
    defaultBg: '#FCE5ED',
    hoverBg: '#F9CCD9',
  },
  HIGH: {
    background: '#F9D8CC',
    border: '#ED7760',
    text: SEVERITY_COLORS.high,
    chip: SEVERITY_COLORS.high,
    defaultBg: '#FCEBE5',
    hoverBg: '#F9D8CC',
  },
  MEDIUM: {
    background: '#FFE5C8',
    border: '#FF9550',
    text: SEVERITY_COLORS.medium,
    chip: SEVERITY_COLORS.medium,
    defaultBg: '#FFF2E0',
    hoverBg: '#FFE5C8',
  },
  WARNING: {
    background: '#F9D8CC',
    border: '#ED7760',
    text: SEVERITY_COLORS.warning,
    chip: SEVERITY_COLORS.warning,
    defaultBg: '#FCEBE5',
    hoverBg: '#F9D8CC',
  },
  LOW: {
    background: '#FFEDC0',
    border: '#FFC444',
    text: SEVERITY_COLORS.low,
    chip: SEVERITY_COLORS.low,
    defaultBg: '#FFF6DC',
    hoverBg: '#FFEDC0',
  },
  INFO: {
    background: '#CCE5FF',
    border: '#5AB3FF',
    text: SEVERITY_COLORS.info,
    chip: SEVERITY_COLORS.info,
    defaultBg: '#E0F0FF',
    hoverBg: '#CCE5FF',
  },
};

// Dark mode color overrides
export const DARK_MODE_COLORS = {
  background: {
    primary: 'var(--background-primary)',
    secondary: 'var(--background-secondary)',
    tertiary: 'var(--background-tertiary)',
  },
  text: {
    primary: 'var(--text-primary)',
    secondary: 'var(--text-secondary)',
    tertiary: 'var(--text-tertiary)',
  },
  border: {
    primary: 'var(--border-color)',
    secondary: 'var(--border-color-secondary)',
  },
};

// Utility function to get color based on severity
export const getSeverityColor = severity => {
  const normalizedSeverity = severity?.toLowerCase();
  return SEVERITY_COLORS[normalizedSeverity] || SEVERITY_COLORS.other;
};

// Utility function to get resource color
export const getResourceColor = resourceType => {
  return RESOURCE_COLORS[resourceType] || CATEGORICAL_COLORS.mutedBlue;
};

// Utility function to get compliance score color
export const getComplianceScoreColor = score => {
  if (score >= 90) return COMPLIANCE_SCORE_COLORS.excellent;
  if (score >= 50) return COMPLIANCE_SCORE_COLORS.good;
  if (score >= 31) return COMPLIANCE_SCORE_COLORS.poor;
  return COMPLIANCE_SCORE_COLORS.critical;
};

// Chart color arrays for easy consumption
export const CHART_COLOR_ARRAYS = {
  severity: [
    SEVERITY_COLORS.critical, // #F4A3B8 - Lighter red/pink from Critical.svg
    SEVERITY_COLORS.high, // #F8A896 - Lighter orange-red from High.svg
    SEVERITY_COLORS.medium, // #FFC085 - Lighter orange from Medium.svg
    SEVERITY_COLORS.low, // #FFE99F - Lighter yellow from Low.svg
    SEVERITY_COLORS.info, // #9FD4FF - Brighter Pastel Blue
  ],
  categorical: [
    CATEGORICAL_COLORS.purple,
    CATEGORICAL_COLORS.lightBlue,
    CATEGORICAL_COLORS.lightGreen,
    CATEGORICAL_COLORS.yellow,
    CATEGORICAL_COLORS.orange,
    CATEGORICAL_COLORS.mutedBlue,
    CATEGORICAL_COLORS.pink,
    CATEGORICAL_COLORS.teal,
  ],
  compliance: [
    COMPLIANCE_SCORE_COLORS.excellent, // #A8E6CF - Pastel Green
    COMPLIANCE_SCORE_COLORS.good, // #FFD19F - Brighter Pastel Orange
    COMPLIANCE_SCORE_COLORS.poor, // #FFEB9F - Brighter Pastel Yellow
    COMPLIANCE_SCORE_COLORS.critical, // #FF9FAA - Brighter Pastel Pink
  ],
  resources: [
    RESOURCE_COLORS.kubernetes,
    RESOURCE_COLORS.scalingGroups,
    RESOURCE_COLORS.hosts,
    RESOURCE_COLORS.containers,
    RESOURCE_COLORS.services,
    RESOURCE_COLORS.networks,
  ],
};

// Default export with all color configurations
export default {
  SEVERITY_COLORS,
  CATEGORICAL_COLORS,
  CHART_COLORS,
  RESOURCE_COLORS,
  COMPLIANCE_SCORE_COLORS,
  STATUS_COLORS,
  UI_COLORS,
  SEVERITY_BACKGROUND_COLORS,
  GRADIENTS,
  DARK_MODE_COLORS,
  CHART_COLOR_ARRAYS,
  getSeverityColor,
  getResourceColor,
  getComplianceScoreColor,
};
